import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { musicPlatformFactory } from '../services/MusicPlatformFactory';

const SpotifyCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          throw new Error(`Spotify authorization failed: ${error}`);
        }

        if (!code) {
          throw new Error('No authorization code received from Spotify');
        }

        console.log('🎵 Processing Spotify authorization callback...');

        // Get the Spotify service and complete the OAuth flow
        const spotifyService = musicPlatformFactory.createService('spotify', {
          clientId: process.env.REACT_APP_SPOTIFY_CLIENT_ID || '',
          redirectUri: process.env.REACT_APP_SPOTIFY_REDIRECT_URI || `${window.location.origin}/spotify/callback`,
          scopes: []
        });

        // Complete the authorization flow
        await (spotifyService as any).handleAuthorizationCallback(code, state);

        // Switch to Spotify as the current platform
        await musicPlatformFactory.switchPlatform('spotify');

        setStatus('success');

        // Redirect to home after a brief success message
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 2000);

      } catch (error) {
        console.error('❌ Spotify callback error:', error);
        setError(error instanceof Error ? error.message : 'Unknown error occurred');
        setStatus('error');

        // Redirect to home after showing error
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 5000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Connecting to Spotify...
              </h2>
              <p className="text-gray-600">
                Please wait while we complete your authorization.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="text-green-600 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Successfully Connected!
              </h2>
              <p className="text-gray-600">
                Your Spotify account has been connected. Redirecting you back to the app...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="text-red-600 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Connection Failed
              </h2>
              <p className="text-gray-600 mb-4">
                {error || 'Failed to connect to Spotify. Please try again.'}
              </p>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
              >
                Return to App
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpotifyCallback;